<template>
    <div class="body v2-list-page">
        <div class="order-page-container">
            <VisitFilters title="Visitas" :filters="filters" :fields="filterFields()" @filtrar="filtrar" @limpar="limpar">
                <template slot="actions">
                    <a-dropdown :trigger="['click']">
                        <a-button :loading="downloadingQr">
                            <i v-if="!downloadingQr" class="fa-solid fa-qrcode"></i>
                            QR Codes das lojas
                            <a-icon type="down" />
                        </a-button>
                        <a-menu slot="overlay">
                            <a-menu-item @click="downloadQrCodes()">Todas as lojas</a-menu-item>
                            <a-menu-divider v-if="qrStores.length" />
                            <a-menu-item v-for="store in qrStores" :key="store.id" @click="downloadQrCodes(store)">
                                {{ store.name }}
                            </a-menu-item>
                        </a-menu>
                    </a-dropdown>
                    <a-button type="primary" @click="showNewVisit = true">
                        <i class="fa-solid fa-plus"></i>
                        Nova visita
                    </a-button>
                </template>
            </VisitFilters>

            <div class="page-section">
                <a-card :bordered="false" style="width: 100%" class="table-card">
                    <div class="table-container">
                        <a-table
                            size="small"
                            row-key="id"
                            :columns="columns"
                            :data-source="rows"
                            :loading="loading"
                            :pagination="pagination"
                            :scroll="{ x: 'max-content' }"
                            :locale="{ emptyText: 'Nenhuma visita no período.' }"
                            @change="tableChange"
                        >
                            <template slot="provider" slot-scope="text, row">
                                <div class="col-fornecedor">
                                    <strong :title="row.provider ? row.provider.name : ''">
                                        {{ row.provider ? truncateText(row.provider.name, 20) : "--" }}
                                    </strong>
                                    <div v-if="row.provider && row.provider.cnpj">{{ row.provider.cnpj }}</div>
                                </div>
                            </template>
                            <template slot="seller" slot-scope="text, row">
                                <div class="col-vendedor" :title="row.seller && row.seller.name">
                                    {{ row.seller ? truncateText(row.seller.name, 14) : "--" }}
                                    <div v-if="row.seller && row.seller.phone" class="col-vendedor-zap">
                                        <i class="fab fa-whatsapp col-vendedor-zap-icon"></i>
                                        <a class="col-vendedor-zap-link" :href="whatsappLink(row.seller.phone)" target="_blank">
                                            {{ formatPhone(row.seller.phone) }}
                                        </a>
                                    </div>
                                </div>
                            </template>
                            <template slot="store" slot-scope="text, row">
                                <div class="sup">
                                    <span>{{ row.store.name }}</span>
                                    <span class="sup-v">{{ row.store.address }}</span>
                                </div>
                            </template>
                            <template slot="status" slot-scope="text, row">
                                <a-tag :color="statusOf(row.status).color">{{ statusOf(row.status).label }}</a-tag>
                            </template>
                            <template slot="requisitions" slot-scope="text, row">
                                <a-tag v-for="requisition in row.requisitions" :key="requisition.id" :color="requisitionStatus(requisition).color">
                                    #{{ requisition.id }} · {{ requisitionStatus(requisition).label }}
                                </a-tag>
                            </template>
                            <template slot="acoes" slot-scope="text, row">
                                <ActionMenu :actions="actions(row)" />
                            </template>
                        </a-table>
                    </div>
                </a-card>
            </div>
        </div>

        <NewVisitModal v-if="showNewVisit" :stores="options.stores || []" @created="openVisit" @close="showNewVisit = false" />
    </div>
</template>

<script>
import VisitService from "@/services/v3/visits/visit.service";
import StoreQrCodeService from "@/services/v3/stores/store-qrcode.service";
import StoreService from "@/services/v3/stores/store.service";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import VisitFilters from "./components/visit-filters.vue";
import NewVisitModal from "./modals/new-visit.modal.vue";
import visitFormat from "./visit-format.mixin";
import { visitListMixin } from "./visit-list.mixin";
import { VISIT_STATUS, isEditableVisit, isVisibleRequisition } from "./visit-status";
import { ALL_SELLER_ORDER_STATUS } from "@/pages/seller-orders/seller-order-status";
import { has_feature, REQUISITIONS } from "@/helpers/features";

export default {
    name: "VisitsPage",
    components: { ActionMenu, VisitFilters, NewVisitModal },
    mixins: [visitFormat, visitListMixin],
    data() {
        return {
            service: new VisitService(),
            statusMap: VISIT_STATUS,
            nouns: ["visita", "visitas"],
            loadError: "Não foi possível carregar as visitas.",
            downloadingQr: false,
            showNewVisit: false,
            qrStores: [],
            seenProviders: [],
            seenSellers: [],
        };
    },
    computed: {
        columns() {
            const sortable = (key) => ({ key, sorter: true, sortDirections: ["descend", "ascend"], sortOrder: this.sortOrderFor(key) });
            return [
                { title: "Visita", ...sortable("id"), customRender: (t, row) => `#${row.id}` },
                { title: "Fornecedor", key: "provider", width: 150, scopedSlots: { customRender: "provider" } },
                { title: "Vendedor", key: "seller", width: 150, scopedSlots: { customRender: "seller" } },
                { title: "Loja", key: "store", scopedSlots: { customRender: "store" } },
                { title: "Status", ...sortable("status"), scopedSlots: { customRender: "status" } },
                { title: "Check-in", align: "center", ...sortable("checked_in_at"), customRender: (t, row) => this.formatDateTime(row.checked_in_at) },
                { title: "Check-out", align: "center", ...sortable("checked_out_at"), customRender: (t, row) => this.formatDateTime(row.checked_out_at) },
                {
                    title: "Duração",
                    key: "duration",
                    align: "center",
                    customRender: (t, row) => this.formatDuration(this.durationBetween(row.checked_in_at, row.checked_out_at)),
                },
                { title: "Requisições", key: "requisitions", scopedSlots: { customRender: "requisitions" } },
                { title: "Valor ofertado", key: "amount", align: "right", customRender: (t, row) => this.brl(this.amountOf(row)) },
                { title: "Ação", key: "acoes", align: "center", fixed: "right", width: 70, scopedSlots: { customRender: "acoes" } },
            ];
        },
    },
    created() {
        new StoreService()
            .load()
            .then(({ data }) => {
                const stores = Array.isArray(data) ? data : data.data || [];
                this.qrStores = stores.map((store) => ({ id: store.id, name: store.name }));
            })
            .catch(() => {});
    },
    methods: {
        filterDefinitions() {
            const requisitionStatuses = Object.keys(ALL_SELLER_ORDER_STATUS).map((value) => ({
                value,
                label: ALL_SELLER_ORDER_STATUS[value].label,
            }));
            return [
                { key: "checkin", type: "period", label: "Check-in", params: ["checked_in_at_from", "checked_in_at_to"] },
                { key: "stores", type: "select", multiple: true, numeric: true, label: "Lojas", param: "store_id", options: () => this.options.stores || [], dropdownWidth: "300px" },
                { key: "providers", type: "select", multiple: true, numeric: true, label: "Fornecedores", param: "provider_id", options: () => this.seenProviders, dropdownWidth: "500px" },
                { key: "sellers", type: "select", multiple: true, numeric: true, label: "Vendedores", param: "seller_id", options: () => this.seenSellers, dropdownWidth: "300px" },
                { key: "statuses", type: "select", multiple: true, label: "Status", param: "status", options: () => this.statusOptions },
                { key: "visit", type: "text", digits: true, label: "Nº da visita", param: "id" },
                { key: "checkout", type: "period", label: "Check-out", params: ["checked_out_at_from", "checked_out_at_to"], advanced: true },
                { key: "requisitionStatuses", type: "select", multiple: true, label: "Status da requisição", param: "requisition_status", options: requisitionStatuses, advanced: true },
            ];
        },
        downloadQrCodes(store) {
            this.downloadingQr = true;
            new StoreQrCodeService()
                .pdf(store ? [store.id] : [])
                .then(({ data }) => this.downloadBlob(data, store ? `qrcode-loja-${store.id}.pdf` : "qrcodes-lojas.pdf"))
                .catch(async (error) => {
                    const body = error && error.response && error.response.data;
                    if (body instanceof Blob) {
                        try {
                            error.response.data = JSON.parse(await body.text());
                        } catch (e) {
                            error.response.data = null;
                        }
                    }
                    this.notifyError(error, "Não foi possível gerar o PDF dos QR codes.");
                })
                .finally(() => {
                    this.downloadingQr = false;
                });
        },
        openVisit(visit) {
            this.$router.push(`/visitas/${visit.id}`);
        },
        collectOptions(rows) {
            const merge = (list, item) => {
                if (item && !list.some((option) => option.value === item.id)) list.push({ value: item.id, label: item.name });
            };
            rows.forEach((row) => {
                merge(this.seenProviders, row.provider);
                merge(this.seenSellers, row.seller);
            });
        },
        amountOf(row) {
            return (row.requisitions || []).reduce((total, requisition) => total + requisition.totals.amount, 0);
        },
        requisitionStatus(requisition) {
            return ALL_SELLER_ORDER_STATUS[requisition.status] || { label: requisition.status, color: "" };
        },
        actions(row) {
            const editable = isEditableVisit(row);
            const requisitions = has_feature(REQUISITIONS) ? (row.requisitions || []).filter((requisition) => isVisibleRequisition(row, requisition)) : [];
            return [
                { label: "Ver detalhes", icon: "visibility", onClick: () => this.$router.push(`/visitas/${row.id}`) },
                ...requisitions.map((requisition) => ({
                    label: editable && requisition.status === "DRAFT" ? "Editar requisição" : "Ver requisição",
                    icon: editable && requisition.status === "DRAFT" ? "edit" : "assignment",
                    onClick: () => this.$router.push(`/requisicoes/${requisition.id}`),
                })),
            ];
        },
    },
};
</script>

<style lang="scss" scoped>
@import "./_page";
</style>
